// PrinterOptions.js - Updated with Bill Printing Toggle (No Placeholder in Dropdown)
import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Save,
  X,
  Edit,
  Trash2,
  Printer,
  Wifi,
  Usb,
  Bluetooth,
  AlertCircle,
  Search,
  Network,
  Link,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_printer_config,
  get_printer_configurations,
  save_update_category_printer_mapping,
  view_category_printer_mappings,
  get_view_category,
  delete_category_printer_mapping,
  delete_printer,
} from "../ServiceConnection/serviceconnection";
import io from "socket.io-client";

const SOCKET_URL = "http://localhost:3998";

export default function PrinterOptions() {
  const { toast } = useToast();

  // Shared states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [socket, setSocket] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("printers");

  // Printers tab states
  const [printers, setPrinters] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    purpose: "",
    type: "usb",
    ip_address: "",
    port: "9100",
    usb_vid: "",
    usb_pid: "",
    ble_address: "",
    ble_service_uuid: "",
    ble_write_char_uuid: "",
  });
  const [editId, setEditId] = useState(0);
  const [discoveredBlePrinters, setDiscoveredBlePrinters] = useState([]);
  const [showDiscoveredModal, setShowDiscoveredModal] = useState(false);
  const [isScanningHelper, setIsScanningHelper] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  // Mapping tab states
  const [mappings, setMappings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mappingForm, setMappingForm] = useState({
    main_id: "",
    category_id: "",
    category_name: "",
    printer_id: "",
    printer_name: "",
    is_default: 0,
    is_bill_print: 0, // Controlled by toggle
  });
  const [mappingEditId, setMappingEditId] = useState(0);
  const [isMappingDeleteOpen, setIsMappingDeleteOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  // Type options
  const typeOptions = [
    { value: "usb", label: "USB", icon: Usb },
    { value: "network", label: "Network (LAN/Wi-Fi)", icon: Network },
    { value: "ble", label: "BLE (Bluetooth)", icon: Bluetooth },
  ];

  // Socket connection
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ["websocket"],
    });

    newSocket.on("connect", () => {
      console.log("Connected to printer helper");
    });

    newSocket.on("connect_error", (err) => {
      console.error("Helper connection error:", err.message);
      toast({
        title: "Connection Error",
        description:
          "Failed to connect to printer helper. Check if agent.js is running.",
        variant: "destructive",
      });
    });

    setSocket(newSocket);

    return () => newSocket.close();
  }, [toast]);

  // Fetch printers
  const fetchPrinters = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 0);

    try {
      const response = await server_post_data(get_printer_configurations, fd);
      console.log("API Response (printers):", response.data); // DEBUG

      if (!response.data.error) {
        const data = response.data.message || [];
        setPrinters(data);
        console.log("Printers set to state:", data); // DEBUG
        if (data.length === 0) {
          toast({
            title: "No Printers",
            description: "No saved printers. Add one above.",
            variant: "default",
          });
        }
      } else {
        toast({
          title: "API Error",
          description: response.data.message || "Failed to load printers",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Fetch printers error:", error);
      toast({
        title: "Error",
        description: "Failed to load printers - check console",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  console.log("Printers state:", printers); // DEBUG
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_view_category, fd);
      console.log("API Response (categories):", response.data);
      if (!response.data.error) {
        const data = Array.isArray(response.data.message)
          ? response.data.message
          : [];
        setCategories(data);
        console.log("Categories set:", data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Fetch mappings
  const fetchMappings = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 0);

    try {
      const response = await server_post_data(
        view_category_printer_mappings,
        fd,
      );
      console.log("API Response (mappings):", response.data);
      if (!response.data.error) {
        const data = response.data.message || [];
        setMappings(data);
        console.log("Mappings set:", data);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching mappings:", error);
      toast({
        title: "Error",
        description: "Failed to load mappings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    fetchPrinters();
    fetchCategories();
    fetchMappings();
  }, [fetchPrinters, fetchCategories, fetchMappings]);

  // Reset printer form
  const resetPrinterForm = () => {
    setFormData({
      name: "",
      purpose: "",
      type: "usb",
      ip_address: "",
      port: "9100",
      usb_vid: "",
      usb_pid: "",
      ble_address: "",
      ble_service_uuid: "",
      ble_write_char_uuid: "",
      width: "",
    });
    setEditId(0);
    setErrors({});
  };

  // Reset mapping form
  const resetMappingForm = () => {
    setMappingForm({
      main_id: "",
      category_id: "",
      category_name: "",
      printer_id: "",
      printer_name: "",
      is_default: 0,
      is_bill_print: 0,
    });
    setMappingEditId(0);
  };

  // Handle printer field change
  const handlePrinterFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  };

  // Handle mapping field change
  const handleMappingFieldChange = (field, value) => {
    setMappingForm((prev) => ({ ...prev, [field]: value }));
  };

  // Validate printer form
  const validatePrinterForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Printer name is required";
    if (!formData.purpose.trim()) newErrors.purpose = "Purpose is required";
    if (formData.type === "network" && !formData.ip_address.trim()) {
      newErrors.ip_address = "IP Address is required";
    }
    if (formData.type === "ble") {
      if (!formData.ble_address.trim())
        newErrors.ble_address = "BLE Address is required";
      if (!formData.ble_service_uuid.trim())
        newErrors.ble_service_uuid = "Service UUID is required";
      if (!formData.ble_write_char_uuid.trim())
        newErrors.ble_write_char_uuid = "Write Char UUID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save printer
  const handleSavePrinter = async () => {
    if (!validatePrinterForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("purpose", formData.purpose);
    fd.append("type", formData.type);
    fd.append("ip_address", formData.ip_address || "");
    fd.append("port", formData.port || "");
    fd.append("usb_vid", formData.usb_vid || "");
    fd.append("usb_pid", formData.usb_pid || "");
    fd.append("ble_address", formData.ble_address || "");
    fd.append("ble_service_uuid", formData.ble_service_uuid || "");
    fd.append("ble_write_char_uuid", formData.ble_write_char_uuid || "");
    fd.append("printer_id", editId);
    fd.append("width", formData.width || "");
    try {
      const response = await server_post_data(save_update_printer_config, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editId === 0 ? "Printer added" : "Printer updated",
        });
        resetPrinterForm();
        fetchPrinters(); // Force table refresh
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save printer",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  // Save mapping
  const handleSaveMapping = async () => {
    if (!mappingForm.printer_id) {
      toast({
        title: "Error",
        description: "Select printer",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("main_id", mappingEditId || "0");
    fd.append("category_id", mappingForm.category_id);
    fd.append("category_name", mappingForm.category_name);
    fd.append("printer_id", mappingForm.printer_id);
    fd.append("printer_name", mappingForm.printer_name);
    fd.append("is_default", mappingForm.is_default ? "1" : "0");
    fd.append("is_bill_print", mappingForm.is_bill_print ? "1" : "0");

    try {
      const response = await server_post_data(
        save_update_category_printer_mapping,
        fd,
      );

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: mappingEditId ? "Mapping updated" : "Mapping saved",
        });
        resetMappingForm();
        fetchMappings();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save mapping",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  // Edit printer
  const handleEdit = (printer) => {
    setFormData({
      name: printer.name,
      purpose: printer.purpose,
      type: printer.type,
      ip_address: printer.ip_address || "",
      port: printer.port || "9100",
      usb_vid: printer.usb_vid || "",
      usb_pid: printer.usb_pid || "",
      ble_address: printer.ble_address || "",
      ble_service_uuid: printer.ble_service_uuid || "",
      ble_write_char_uuid: printer.ble_write_char_uuid || "",
      width: printer.width || "",
    });
    setEditId(printer.primary_id);
    setErrors({});
  };

  // Edit mapping
  const handleEditMapping = (mapping) => {
    setMappingForm({
      main_id: mapping.primary_id,
      category_id: mapping.category_id,
      category_name: mapping.category_name,
      printer_id: mapping.printer_id,
      printer_name: mapping.printer_name,
      is_default: mapping.is_default,
      is_bill_print: mapping.is_bill_print,
    });
    setMappingEditId(mapping.primary_id);
  };

  // Delete printer
  const handleDeletePrinter = async () => {
    if (!selectedPrinter) return;

    setPrinters(
      printers.filter((p) => p.primary_id !== selectedPrinter.primary_id),
    );
    toast({
      title: "Success",
      description: "Printer deleted",
    });
    setIsDeleteDialogOpen(false);
    setSelectedPrinter(null);
  };

  // Delete mapping
  const handleDeleteMapping = async () => {
    if (!selectedMapping) return;

    setMappings(
      mappings.filter((m) => m.primary_id !== selectedMapping.primary_id),
    );
    toast({
      title: "Success",
      description: "Mapping deleted",
    });
    setIsMappingDeleteOpen(false);
    setSelectedMapping(null);
  };

  // Tab switch
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "mapping") {
      fetchMappings();
    }
  };

  // Add this new function
  const handleDeletePrinterConfirm = async () => {
    if (!selectedPrinter) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("printer_id", selectedPrinter.primary_id);

    try {
      // ← Change this URL to your actual printer delete endpoint
      const response = await server_post_data(delete_printer, fd); // ← ADJUST URL

      if (response.data.error) {
        toast({
          title: "Delete Failed",
          description: response.data.message || "Could not delete printer",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Printer deleted successfully",
        });
        // Refresh list
        await fetchPrinters();
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to delete printer. Check connection.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedPrinter(null);
    }
  };

  // Replace the fake delete with real API call
  const handleDeleteMappingConfirm = async () => {
    if (!selectedMapping) return;

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("mapping_id", selectedMapping.primary_id); // ← important: mapping_id, NOT primary_id

    try {
      const response = await server_post_data(
        delete_category_printer_mapping,
        fd,
      );

      if (response.data.error) {
        toast({
          title: "Delete Failed",
          description: response.data.message || "Could not delete mapping",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Mapping deleted successfully",
        });
        // Refresh list
        await fetchMappings();
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to delete mapping",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsMappingDeleteOpen(false);
      setSelectedMapping(null);
    }
  };

  // Type icon
  const getTypeIcon = (type) => {
    const option = typeOptions.find((opt) => opt.value === type);
    return option ? option.icon : Printer;
  };

  // Type label
  const getTypeLabel = (type) => {
    const option = typeOptions.find((opt) => opt.value === type);
    return option ? option.label : type;
  };

  // Purpose display
  const getPurposeDisplay = (purpose) => {
    return purpose
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Printer & Mapping Configuration
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Manage printers and assign them to categories for KOT routing
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex space-x-4">
          <Button
            variant={activeTab === "printers" ? "default" : "ghost"}
            onClick={() => handleTabChange("printers")}
            className="rounded-b-none"
          >
            Printers
          </Button>
          <Button
            variant={activeTab === "mapping" ? "default" : "ghost"}
            onClick={() => handleTabChange("mapping")}
            className="rounded-b-none"
          >
            Category → Printer Mapping
          </Button>
        </div>
      </div>

      {/* Printers Tab */}
      {activeTab === "printers" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Printer className="w-4 h-4 text-primary" />
              {editId === 0 ? "Add New Printer" : "Edit Printer"}
            </CardTitle>
            <CardDescription>
              Configure printer connection details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Debug info */}
            <p className="text-sm text-muted-foreground">
              Debug: Printers in state = {printers.length} (loading:{" "}
              {showLoaderAdmin ? "yes" : "no"})
            </p>

            {/* Printer Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>
                  Printer Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g. Kitchen Printer"
                  value={formData.name}
                  onChange={(e) =>
                    handlePrinterFieldChange("name", e.target.value)
                  }
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Purpose (Routing Key){" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g. kitchen_kot, customer_receipt"
                  value={formData.purpose}
                  onChange={(e) =>
                    handlePrinterFieldChange(
                      "purpose",
                      e.target.value.toLowerCase(),
                    )
                  }
                  className={errors.purpose ? "border-destructive" : ""}
                />
                {errors.purpose && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.purpose}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Width
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g. 58mm, 80mm"
                  value={formData.width}
                  onChange={(e) =>
                    handlePrinterFieldChange(
                      "width",
                      e.target.value.toLowerCase(),
                    )
                  }
                  className={errors.width ? "border-destructive" : ""}
                />
                {errors.width && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.width}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Connection Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    handlePrinterFieldChange("type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type-specific fields */}
            {formData.type === "network" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
                <div className="space-y-2">
                  <Label>
                    IP Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. 192.168.1.150"
                    value={formData.ip_address}
                    onChange={(e) =>
                      handlePrinterFieldChange("ip_address", e.target.value)
                    }
                    className={errors.ip_address ? "border-destructive" : ""}
                  />
                  {errors.ip_address && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.ip_address}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Port (default 9100)</Label>
                  <Input
                    type="number"
                    value={formData.port}
                    onChange={(e) =>
                      handlePrinterFieldChange("port", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            {formData.type === "usb" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
                <div className="space-y-2">
                  <Label>USB VID (optional)</Label>
                  <Input
                    placeholder="e.g. 1234"
                    value={formData.usb_vid}
                    onChange={(e) =>
                      handlePrinterFieldChange("usb_vid", e.target.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>USB PID (optional)</Label>
                  <Input
                    placeholder="e.g. 5678"
                    value={formData.usb_pid}
                    onChange={(e) =>
                      handlePrinterFieldChange("usb_pid", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            {formData.type === "ble" && (
              <div className="space-y-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>
                      BLE MAC Address{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="e.g. 3562791709DB78D8..."
                      value={formData.ble_address}
                      onChange={(e) =>
                        handlePrinterFieldChange(
                          "ble_address",
                          e.target.value.toUpperCase(),
                        )
                      }
                      className={errors.ble_address ? "border-destructive" : ""}
                    />
                    {errors.ble_address && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.ble_address}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Service UUID <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.ble_service_uuid}
                      onChange={(e) =>
                        handlePrinterFieldChange(
                          "ble_service_uuid",
                          e.target.value,
                        )
                      }
                      className={
                        errors.ble_service_uuid ? "border-destructive" : ""
                      }
                    />
                    {errors.ble_service_uuid && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.ble_service_uuid}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Write Char UUID{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={formData.ble_write_char_uuid}
                      onChange={(e) =>
                        handlePrinterFieldChange(
                          "ble_write_char_uuid",
                          e.target.value,
                        )
                      }
                      className={
                        errors.ble_write_char_uuid ? "border-destructive" : ""
                      }
                    />
                    {errors.ble_write_char_uuid && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.ble_write_char_uuid}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 pt-4 border-t">
                  <Button
                    onClick={() => {
                      if (!socket || !socket.connected) {
                        toast({
                          title: "Error",
                          description: "Not connected to printer helper.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsScanningHelper(true);
                      socket.emit("scan-ble-printers", (foundPrinters) => {
                        setIsScanningHelper(false);
                        if (foundPrinters.length === 0) {
                          toast({
                            title: "No Printers",
                            description: "No BLE printers found.",
                            variant: "destructive",
                          });
                        } else {
                          setDiscoveredBlePrinters(foundPrinters);
                          setShowDiscoveredModal(true);
                        }
                      });
                    }}
                    disabled={isScanningHelper}
                    variant="outline"
                    className="gap-2"
                    size="lg"
                  >
                    <Search
                      className={`w-4 h-4 ${isScanningHelper ? "animate-pulse" : ""}`}
                    />
                    {isScanningHelper
                      ? "Scanning..."
                      : "Scan BLE Printers via Helper"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Uses your running node agent.js — shows real MAC addresses
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              {editId !== 0 && (
                <Button
                  variant="outline"
                  onClick={resetPrinterForm}
                  className="gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSavePrinter}
                disabled={showLoaderAdmin}
                className="gap-2"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : editId === 0 ? (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Printer
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Update Printer
                  </>
                )}
              </Button>
            </div>

            {/* Printer Table */}
            <div className="mt-8">
              <h4 className="text-base font-medium mb-4">Saved Printers</h4>
              {showLoaderAdmin ? (
                <div className="text-center py-10 text-muted-foreground">
                  Loading printers...
                </div>
              ) : printers.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  No printer configurations found. Add your first printer above.
                </div>
              ) : (
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground w-[80px]">
                          Sr.No
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Name
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Purpose
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Type
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Details
                        </TableHead>
                        <TableHead className="text-muted-foreground">
                          Status
                        </TableHead>
                        <TableHead className="text-muted-foreground text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {printers.map((printer, index) => {
                        const Icon = getTypeIcon(printer.type);
                        return (
                          <TableRow
                            key={printer.primary_id}
                            className="border-border"
                          >
                            <TableCell className="font-medium">
                              {index + 1}
                            </TableCell>
                            <TableCell className="font-medium">
                              {printer.name}
                            </TableCell>
                            <TableCell>
                              {getPurposeDisplay(printer.purpose)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="gap-1">
                                <Icon className="w-3 h-3" />
                                {getTypeLabel(printer.type)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {printer.type === "network" &&
                                `${printer.ip_address}:${printer.port}`}
                              {printer.type === "usb" &&
                                `VID:${printer.usb_vid || "-"} PID:${printer.usb_pid || "-"}`}
                              {printer.type === "ble" &&
                                `MAC: ${printer.ble_address || "-"}`}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={printer.is_active === 1}
                                onCheckedChange={() => {
                                  setSelectedPrinter(printer);
                                  setIsToggleDialogOpen(true);
                                }}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(printer)}
                                  className="hover:bg-primary/10 hover:text-primary"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedPrinter(printer);
                                    setIsDeleteDialogOpen(true);
                                  }}
                                  className="hover:bg-destructive/10 hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mapping Tab */}
      {activeTab === "mapping" && (
        <div className="space-y-6">
          {/* Mapping Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Link className="w-4 h-4 text-primary" />
                {mappingEditId === 0
                  ? "Map Category to Printer"
                  : "Edit Mapping"}
              </CardTitle>
              <CardDescription>
                Assign a printer to a category for automatic KOT routing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={mappingForm.category_id}
                    onValueChange={(value) => {
                      const cat = categories.find((c) => c.primary_id == value);
                      handleMappingFieldChange("category_id", value);
                      handleMappingFieldChange(
                        "category_name",
                        cat?.item_category || "",
                      );
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.primary_id} value={cat.primary_id}>
                          {cat.item_category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Printer</Label>
                  <Select
                    value={mappingForm.printer_id}
                    onValueChange={(value) => {
                      const pr = printers.find((p) => p.primary_id == value);
                      handleMappingFieldChange("printer_id", value);
                      handleMappingFieldChange("printer_name", pr?.name || "");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Printer" />
                    </SelectTrigger>
                    <SelectContent>
                      {printers.map((p) => (
                        <SelectItem key={p.primary_id} value={p.primary_id}>
                          {p.name} ({p.purpose})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={mappingForm.is_default === 1}
                    onCheckedChange={(checked) =>
                      handleMappingFieldChange("is_default", checked ? 1 : 0)
                    }
                  />
                  <Label>Default Printer for Category</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={mappingForm.is_bill_print === 1}
                    onCheckedChange={(checked) =>
                      handleMappingFieldChange("is_bill_print", checked ? 1 : 0)
                    }
                  />
                  <Label>Use this printer for Bill Printing</Label>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                {mappingEditId !== 0 && (
                  <Button variant="outline" onClick={resetMappingForm}>
                    Cancel
                  </Button>
                )}
                <Button onClick={handleSaveMapping}>
                  {mappingEditId ? "Update Mapping" : "Save Mapping"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Mapping Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Category → Printer Mappings
              </CardTitle>
              <CardDescription>
                Categories linked to specific printers for KOT routing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Printer</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead>Bill Print</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappings.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground"
                        >
                          No mappings found. Create one above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      mappings.map((mapping) => (
                        <TableRow key={mapping.primary_id}>
                          <TableCell className="font-medium">
                            {mapping.category_name}
                          </TableCell>
                          <TableCell>{mapping.printer_name}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                mapping.is_default ? "default" : "secondary"
                              }
                            >
                              {mapping.is_default ? "Yes" : "No"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                mapping.is_bill_print ? "default" : "secondary"
                              }
                            >
                              {mapping.is_bill_print ? "Yes" : "No"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMapping(mapping)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => {
                                setSelectedMapping(mapping);
                                setIsMappingDeleteOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Printers Delete Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Printer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{selectedPrinter?.name}</strong>? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePrinterConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mapping Delete Dialog */}
      <AlertDialog
        open={isMappingDeleteOpen}
        onOpenChange={setIsMappingDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete mapping for{" "}
              <strong>{selectedMapping?.category_name}</strong> →{" "}
              <strong>{selectedMapping?.printer_name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMappingConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discovered BLE Printers Dialog */}
      <Dialog open={showDiscoveredModal} onOpenChange={setShowDiscoveredModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Your BLE Printer</DialogTitle>
            <DialogDescription>
              Choose from the discovered Bluetooth printers
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border overflow-y-auto flex-1">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>Printer Name</TableHead>
                  <TableHead>MAC Address</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discoveredBlePrinters.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground"
                    >
                      No printers discovered
                    </TableCell>
                  </TableRow>
                ) : (
                  discoveredBlePrinters.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {p.address}
                        </code>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => {
                            handlePrinterFieldChange("ble_address", p.address);
                            handlePrinterFieldChange(
                              "ble_service_uuid",
                              "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
                            );
                            handlePrinterFieldChange(
                              "ble_write_char_uuid",
                              "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f",
                            );
                            setShowDiscoveredModal(false);
                            toast({
                              title: "Printer Selected",
                              description: `${p.name} has been selected`,
                            });
                          }}
                          className="gap-2"
                        >
                          <Bluetooth className="w-4 h-4" />
                          Use This Printer
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
